<script setup lang="ts">
import api from "@/api";
import { SimplifiedDate } from "@/assets/js/dateHelper";
import type {
	TClassManagerFilters,
	TPagination,
} from "@/assets/js/globalTypes";
import { setupChangesFields, useIsMobile } from "@/assets/js/helpers";
import { continueDraft, openBookingModal } from "@/assets/js/scenario2";
import { openPreviewClassModal } from "@/components/modals/modalDefinitions";
import { $t } from "@/i18n";
import { useCalendarStore } from "@/store/calendar";
import { useClassStore } from "@/store/class";
import { useSchoolStore } from "@/store/school";
import { useSessionStore } from "@/store/session";
import dayjs from "dayjs";
import type Popover from "primevue/popover";

definePage({
	name: "school.class-management.class-requests",
});

type TClassManagerFiltersParams = {
	date_from: string | null;
	date_to: string | null;
	tag_ids: string;
	usr_ids: string;
};

const sessionStore = useSessionStore();
const calendarStore = useCalendarStore();
const isMobile = useIsMobile();

const schoolId = ref(sessionStore.userData.scl_id);
const isLoading = ref(true);
const tableData = ref([]);
const status = ref<"open" | "accepted" | "rejected" | "draft">("open");
const columns = ref([
	{
		field: "startDate",
		header: $t("app.school.classManagement.table.headers.startDate"),
	},
	{
		field: "teacherName",
		header: $t("app.school.classManagement.table.headers.teacherName"),
	},
	{
		field: "program",
		header: $t("app.school.classManagement.table.headers.program"),
	},
	{
		field: "city",
		header: $t("app.school.classManagement.table.headers.city"),
	},
	{
		field: "numOfClasses",
		header: $t("app.school.classManagement.table.headers.numOfClasses"),
	},
	{
		field: "title",
		header: $t("app.school.classManagement.table.headers.title"),
	},
]);

const page = ref(1);
const pagination = ref<TPagination>();
const programs = ref<{ tag_id: number; tag_name: string }[]>([]);
const teachers = ref<{ usr_id: number; usr_fullname: string }[]>([]);
const selectedClassManagerFiltersChanges = ref<TClassManagerFilters>({
	programs: [],
	teachers: [],
	from: null,
	to: null,
});
provide(
	"selectedClassManagerFiltersChanges",
	selectedClassManagerFiltersChanges,
);
const selectedClassManagerFilters = ref<TClassManagerFilters>({
	programs: [],
	teachers: [],
	from: null,
	to: null,
});
const classManagerFilterKey = ref(1);
const op3 = ref<InstanceType<typeof Popover> | null>(null);
const isVisibleFiltersPanel = ref(false);
const schoolStore = useSchoolStore();

onMounted(async () => {
	isLoading.value = true;
	const res = await api.getSchoolClassRequests({
		scl_id: schoolId.value,
		crq_status: status.value.toUpperCase(),
		page: 1,
		per_page: 10,
		sort: "desc",
		// sortby: "crq_id",
	});
	pagination.value = res.data.meta.pagination;
	for (const item of res.data.data) {
		formatDataForDisplay(item);
	}
	await getCalendarFilters();
	isLoading.value = false;
});

watch(
	() => calendarStore.stateRefreshHandle,
	async () => {
		page.value = 1;
		await getData();
	},
);

watch(status, async () => {
	page.value = 1;
	await getData();
});

watch(
	() => schoolStore.repushRefreshHandle,
	async () => {
		await getData();
	},
);

const formatDataForDisplay = (data: any) => {
	const returnObj = {
		startDate: SimplifiedDate.fromUtcToLocal(data.from).format("DD/MM/YYYY"),
		// teacherName: data?.classes
		// 	? data.classes[0].usr_fullname === null
		// 		? $t("app.school.classManagement.table.teacherNameDefault")
		// 		: data.classes[0].usr_fullname
		// 	: $t("app.school.classManagement.table.teacherNameDefault"),
		teacherName: data?.usr_fullname
			? data.usr_fullname === null
				? $t("app.school.classManagement.table.teacherNameDefault")
				: data.usr_fullname
			: $t("app.school.classManagement.table.teacherNameDefault"),
		program: "",
		city: "",
		numOfClasses: data.total_count,
		title: data.crq_title,
		id: data.crq_id,
	};

	if (data.classes) {
		for (const item of data.classes) {
			if (returnObj.city.includes(item.cit_name)) {
				continue;
			}
			if (returnObj.city === "") {
				returnObj.city = item.cit_name;
			} else {
				returnObj.city += ", " + item.cit_name;
			}
		}
	}

	if (data.programs) {
		for (const item of data.programs) {
			if (returnObj.program === "") {
				returnObj.program = item.tag_name;
			} else {
				returnObj.program += ", " + item.tag_name;
			}
		}
	}

	if (returnObj.program === "") {
		returnObj.program = "no program";
	}
	tableData.value.push(returnObj);
};

const changePage = async (event: any) => {
	page.value = event.page + 1;
	// console.log(event);
	// console.log(page.value);
	// console.log({
	// 	scl_id: schoolId.value,
	// 	crq_status: status.value.toUpperCase(),
	// 	page: page.value,
	// 	per_page: 10,
	// });

	// console.log(pagination.value);
	await getData();
	// console.log(pagination.value);
};

const getData = async () => {
	isLoading.value = true;

	// Prepare the request parameters
	const processedParams: TClassManagerFiltersParams = preprocessFilterParams(
		selectedClassManagerFilters.value,
	);

	const res = await api.getSchoolClassRequests({
		scl_id: schoolId.value,
		crq_status: status.value.toUpperCase(),
		page: page.value,
		per_page: 10,
		...processedParams,
	});
	pagination.value = res.data.meta.pagination;
	tableData.value = [];
	for (const item of res.data.data) {
		formatDataForDisplay(item);
	}
	isLoading.value = false;
};

const classStore = useClassStore();
async function handleFindTeacher(id: number) {
	try {
		const res = await api.getSchoolClassRequest(id);
		// console.log(res);
		const data = res.data.data;
		classStore.loadClassRequest(data);
		const from = data?.from ? dayjs(data.from) : dayjs();
		continueDraft(from, data?.crq_title);
	} catch (error) {
		console.error(error);
	}
}

const handleRowClick = async (event: any) => {
	// console.log(event);
	// const pattern = /start|end|date|from|to/i;
	const pattern = /(start|end|date|\bfrom\b|\bto\b)/i;
	const res = await api.getSchoolClassRequest(event.data.id);
	const data = res.data.data;
	calendarStore.adjustTimeForUnknownObject(pattern, data);
	classStore.loadClassRequest(data, false);

	openPreviewClassModal({
		title: {
			type: getType(data.crq_status),
			title: data.crq_title,
		},
		allowActions: false,
		onNext: () => {},
		onBack: () => {},
	});
};

const getType = (status: string) => {
	switch (status.toLowerCase()) {
		case "open": {
			return $t("app.school.classManagement.status.pending");
		}
		case "accepted": {
			return $t("app.school.classManagement.status.accepted");
		}
		case "rejected": {
			return $t("app.school.classManagement.status.rejected");
		}
		case "draft": {
			return $t("app.school.classManagement.status.draft");
		}
		default: {
			return "";
		}
	}
};

watch(
	() => useSchoolStore().cancelClassRefreshHandle,
	async () => {
		await getData();
	},
);

// Filters

const preprocessFilterParams = (params: TClassManagerFilters) => {
	return {
		date_from: params.from
			? SimplifiedDate.fromLocalToUtc(params.from).format("YYYY-MM-DD")
			: null,
		date_to: params.to
			? SimplifiedDate.fromLocalToUtc(params.to).format("YYYY-MM-DD")
			: null,
		usr_ids: params.teachers.map((t) => t.usr_id).join(",") || "",
		tag_ids: params.programs.map((p) => p.tag_id).join(",") || "",
	};
};

async function getCalendarFilters() {
	try {
		const res = await api.getCalendarFilters({ scl_id: schoolId.value });

		programs.value = res.data.data?.programs;
		teachers.value = res.data.data?.teachers;
	} catch (error) {
		console.error(error);
	}
}

// Toggles the filter panel visibility
const toggle = (event) => {
	isVisibleFiltersPanel.value = true;
	if (!isMobile.value) op3.value.toggle(event);
};

async function resetFilters() {
	selectedClassManagerFiltersChanges.value = {
		programs: [],
		teachers: [],
		from: null,
		to: null,
	};

	selectedClassManagerFilters.value = {
		programs: [],
		teachers: [],
		from: null,
		to: null,
	};

	schoolStore.filtersSelectedDateOption = "";

	classManagerFilterKey.value += 1;

	await getData();
}

async function applyFilters(event?: MouseEvent) {
	setupChangesFields(
		selectedClassManagerFiltersChanges.value,
		selectedClassManagerFilters,
	);
	isVisibleFiltersPanel.value = false;
	if (event && !isMobile.value) toggle(event);
	page.value = 1;
	await getData();
}
</script>

<template>
	<div id="class-manager-page">
		<div class="class-manager-header my-4">
			<div class="tablike-group">
				<div
					class="tablike-btn"
					:class="{ active: status === 'open' }"
					@click="status = 'open'"
				>
					{{ $t("app.school.classManagement.status.pending") }}
				</div>
				<div
					class="tablike-btn"
					:class="{ active: status === 'accepted' }"
					@click="status = 'accepted'"
				>
					{{ $t("app.school.classManagement.status.accepted") }}
				</div>
				<div
					class="tablike-btn"
					:class="{ active: status === 'rejected' }"
					@click="status = 'rejected'"
				>
					{{ $t("app.school.classManagement.status.rejected") }}
				</div>
				<div
					class="tablike-btn"
					:class="{ active: status === 'draft' }"
					@click="status = 'draft'"
				>
					{{ $t("app.school.classManagement.status.draft") }}
				</div>
			</div>
			<div class="header-actions">
				<p-button
					:pt="{
						root: {
							class: 'btn-root',
							style: 'width: fit-content;',
						},
					}"
					:disabled="!schoolStore.isSubActive"
					@click="openBookingModal"
				>
					<i-mdi-plus />
					{{ $t("app.school.classManagement.header.createBtn") }}
				</p-button>
				<p-button
					severity="contrast"
					rounded
					variant="outlined"
					:pt="{
						root: {
							class: 'filters-btn-root',
						},
					}"
					@click="toggle"
				>
					{{ $t("app.school.teacherSearch.filters") }}
					<i-ic-baseline-keyboard-arrow-down />
				</p-button>
			</div>
		</div>
		<div
			v-if="isMobile"
			class="mobile-card-wrap"
		>
			<class-manager-card
				:status="status"
				:data="tableData"
				@find-teacher="handleFindTeacher"
			/>
			<p-paginator
				:total-records="pagination?.total"
				:rows="10"
				:first="(page - 1) * 10"
				@page="changePage"
			/>
		</div>
		<div
			v-else
			class="table-wrap"
		>
			<p-data-table
				v-if="isLoading"
				:value="[
					{ id: 1 },
					{ id: 2 },
					{ id: 3 },
					{ id: 4 },
					{ id: 5 },
					{ id: 6 },
					{ id: 7 },
					{ id: 8 },
				]"
			>
				<p-column
					v-for="column of columns"
					:key="column.field"
					:field="column.field"
					:header="column.header"
					:pt="{
						columnTitle: {
							class: 'class-management-column-title',
						},
						root: {
							style: 'height: 50.59px;',
						},
					}"
				>
					<template #body>
						<p-skeleton></p-skeleton>
					</template>
				</p-column>
				<p-column
					field="action"
					header=""
					:pt="{
						bodyCell: {
							class: 'class-management-archive-column-body-cell',
						},
					}"
				>
					<template #body>
						<p-skeleton></p-skeleton>
					</template>
				</p-column>
			</p-data-table>
			<p-data-table
				v-else
				:value="tableData"
				paginator
				:total-records="pagination.total"
				:rows="10"
				:first="(page - 1) * 10"
				lazy
				:pt="{
					root: {
						class: 'class-management-data-table-root',
					},
					bodyRow: {
						style: 'cursor: pointer;',
					},
				}"
				@page="changePage"
				@row-click="handleRowClick"
			>
				<p-column
					v-for="column of columns"
					:key="column.field"
					:field="column.field"
					:header="column.header"
					:pt="{
						columnTitle: {
							class: 'class-management-column-title',
						},
					}"
				></p-column>
				<p-column
					v-if="status === 'draft' || status === 'rejected'"
					field="action"
					header=""
					:pt="{
						bodyCell: {
							class: 'class-management-archive-column-body-cell',
							style: 'text-overflow: unset;',
						},
					}"
				>
					<template #body="{ data }">
						<p-button
							:disabled="!schoolStore.isSubActive"
							@click="handleFindTeacher(data.id)"
						>
							{{ $t("app.booking.findTeacher") }}
						</p-button>
					</template>
				</p-column>
			</p-data-table>
		</div>
		<p-drawer
			v-if="isMobile"
			:visible="isVisibleFiltersPanel"
			:dismissable="true"
			:modal="true"
			position="left"
			:pt="{
				root: {
					class: 'filters-drawer-root',
				},
				header: {
					class: 'filters-drawer-header',
				},
				content: {
					class: 'filters-drawer-content',
				},
				footer: {
					class: 'filters-drawer-footer',
				},
			}"
			@update:visible="isVisibleFiltersPanel = !isVisibleFiltersPanel"
		>
			<template #header>
				<div class="header-content-wrapper">
					{{ $t("app.school.teacherSearch.filters") }}
				</div>
			</template>
			<class-manager-filters
				:key="classManagerFilterKey"
				:programs="programs"
				:teachers="teachers"
			></class-manager-filters>
			<template #footer>
				<div class="btn-wrapper">
					<p-button
						outlined
						:pt="{
							root: {
								class: ['btn-root', 'btn-root-reset'],
							},
						}"
						@click="resetFilters()"
					>
						<i-material-symbols-forward-media-rounded />
						{{
							$t("app.school.teacherSearch.filtersButton", {
								action: $t("app.base.reset"),
							})
						}}
					</p-button>
					<p-button
						:pt="{
							root: {
								class: ['btn-root', 'btn-root-apply'],
							},
							label: {
								class: ['btn-label', 'btn-label-apply'],
							},
						}"
						@click="applyFilters()"
					>
						{{
							$t("app.school.teacherSearch.filtersButton", {
								action: $t("app.base.apply"),
							})
						}}
					</p-button>
				</div>
			</template>
		</p-drawer>
		<p-popover
			v-else
			ref="op3"
			:pt="{
				root: {
					class: 'filters-popover-root',
				},
				header: {
					class: 'teacher-search-popover-header',
				},
				content: {
					class: 'filters-popover-content',
				},
			}"
		>
			<div class="btn-wapper">
				<p-button
					:pt="{
						root: {
							class: ['btn-root', 'btn-root-apply'],
						},
						label: {
							class: ['btn-label', 'btn-label-apply'],
						},
					}"
					@click="(e) => applyFilters(e)"
				>
					{{
						$t("app.school.teacherSearch.filtersButton", {
							action: $t("app.base.apply"),
						})
					}}
				</p-button>
				<p-button
					outlined
					:pt="{
						root: {
							class: ['btn-root', 'btn-root-reset'],
						},
					}"
					@click="resetFilters()"
				>
					<i-material-symbols-forward-media-rounded />
					{{
						$t("app.school.teacherSearch.filtersButton", {
							action: $t("app.base.reset"),
						})
					}}
				</p-button>
			</div>
			<class-manager-filters
				:key="classManagerFilterKey"
				:programs="programs"
				:teachers="teachers"
			></class-manager-filters>
		</p-popover>
	</div>
</template>

<style lang="scss" scoped>
$border-color: #6e6e8b;

#class-manager-page {
	display: flex;
	flex-direction: column;
	padding: 20px;
	height: calc(100vh - $nav-top-bar - 1px);
	overflow-x: auto;
	box-sizing: border-box;
	width: 100%;

	.class-manager-header {
		display: flex;
		flex-direction: column-reverse;
		justify-content: space-between;
		gap: 30px;

		.tablike-group {
			display: flex;
			gap: 0;
			justify-content: space-between;

			.tablike-btn {
				width: 90px;
				height: 40px;
				font-size: 1rem;
				font-weight: 600;
				color: $daidai-dark-blue;
				display: flex;
				align-items: center;
				justify-content: center;
				cursor: pointer;

				&.active {
					border-bottom: 2px solid $new-blue;
				}
			}
		}

		.header-actions {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			gap: 1rem;
			flex-direction: row-reverse;
			justify-content: space-between;
			box-sizing: border-box;

			.filters-btn-root {
				border-color: $border-color;
				color: $border-color;
				font-size: 18px;
				font-family: Archivo, sans-serif;
				min-width: 111px;
				height: 40px;
			}

			.btn-root {
				width: 222.05px;
				border: none;
				font-size: 18px;
				font-family: Archivo, sans-serif;
				font-weight: 700;
				color: $background-color;
				height: 40px;
				white-space: nowrap;
			}
		}
	}

	.table-wrap {
		width: 100%;
		overflow-x: auto;

		.class-management-data-table-root {
			min-height: 461.75px;

			:deep() {
				.p-datatable-header-cell {
					background-color: transparent;
					border-bottom: 1px solid $new-blue;

					.class-management-column-title {
						font-size: 16px;
						font-weight: 700;
						font-family: Inter, sans-serif;
						color: $new-blue;
						min-width: 120px;
					}
				}

				.p-table-body-cell {
					font-size: 16px;
					font-weight: 500;
					font-family: Inter, sans-serif;
					user-select: text;
					overflow: hidden;
					white-space: nowrap;
					text-overflow: ellipsis;
					max-width: 120px;

					&.class-management-archive-column-body-cell {
						min-width: 200px;
						text-align: center;

						.p-button {
							.faico {
								font-size: 26px;
							}
						}
					}

					&.class-management-edit-column-body-cell {
						padding: 0 19px 0 0;
						width: 20px;

						.p-button {
							padding: 0;

							.faico {
								font-size: 26px;
							}
						}
					}
				}
			}
		}
	}
}

@media screen and (width >= 768px) {
	#class-manager-page {
		max-width: calc(100vw - $mini-sidebar-width);

		.class-manager-header {
			flex-direction: column-reverse;

			.tablike-group {
				gap: 20px;
				justify-content: space-evenly;

				.tablike-btn {
					width: 90px;
					font-size: 1rem;
				}
			}

			.header-actions {
				flex-direction: row;
				justify-content: space-between;
			}
		}
	}
}

@media screen and (width >= 1157px) {
	#class-manager-page {
		max-width: calc(100vw - $expanded-sidebar-width);
	}
}

@media screen and (width >= 1600px) {
	#class-manager-page {
		.class-manager-header {
			flex-direction: row;
			.tablike-group {
				gap: 30px;
				.tablike-btn {
					width: 174px;
					font-size: 18px;
				}
			}
			.header-actions {
				flex-direction: row;
			}
		}
	}
}
</style>
